import { Component, Input, OnInit } from '@angular/core';
import { NgbActiveModal, NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { NgxSpinnerService } from 'ngx-spinner';
import { ToastrService } from 'ngx-toastr';
import { TranslateService } from '@ngx-translate/core';
import { NgbdModalContent } from 'projects/Autofacets-Core/src/app/shared/delete-modal.component';
import { MechanicLineAssignmentDataService } from '../../data-services/mechanic-line-assignment-data.service';
import {
  AssignmentRow,
  EmployeeResource,
  MechanicLineAssignmentItem,
  MechanicLineAssignmentSaveRequest
} from '../../business-entities/mechanic-line-assignment.model';

@Component({
  selector: 'app-service-resource-assignment-modal',
  templateUrl: './service-resource-assignment-modal.component.html',
  styleUrls: ['./service-resource-assignment-modal.component.css']
})
export class ServiceResourceAssignmentModalComponent implements OnInit {

  @Input() appointmentId: number;
  @Input() appointmentJobId: number;
  @Input() appointmentJobDetailId: number;
  @Input() ifsWorkOrderNumber: string;
  @Input() dmsJobLineNo: string;
  @Input() dmsLineNo: string;
  @Input() lineLabel: string;
  @Input() demandNo: number;
  @Input() jobDetailId: number;
  @Input() costType: string;
  @Input() qty: number;
  @Input() existingAssignments: MechanicLineAssignmentItem[] = [];
  @Input() availableResources: EmployeeResource[] = [];
  @Input() isMechanicLineAssignmentInDmsEnabled: boolean = false;
  @Input() isReadOnly: boolean = false;

  rows: AssignmentRow[] = [];
  isSaving = false;

  constructor(
    public activeModal: NgbActiveModal,
    private readonly modalService: NgbModal,
    private readonly dataService: MechanicLineAssignmentDataService,
    private readonly spinner: NgxSpinnerService,
    private readonly toastr: ToastrService,
    private readonly translate: TranslateService
  ) { }

  async ngOnInit(): Promise<void> {
    this.spinner.show();
    try {
      const response = await this.dataService.getMechanicLineAssignmentByDetail(
        this.appointmentId, this.appointmentJobDetailId);
      const items: MechanicLineAssignmentItem[] = response?.responseObject || [];
      if (items.length > 0) {
        this.rows = items.map(a => ({
          id: a.id,
          employeeNo: a.employeeNo,
          portionPercent: a.portionPercent,
          dmsSyncStatus: a.dmsSyncStatus,
          dmsSyncDate: a.dmsSyncDate
        }));
      } else {
        this.rows = [{ employeeNo: '', portionPercent: 100 }];
      }
    } catch {
      // Fallback to passed-in data if API fails
      if (this.existingAssignments && this.existingAssignments.length > 0) {
        this.rows = this.existingAssignments.map(a => ({
          id: a.id,
          employeeNo: a.employeeNo,
          portionPercent: a.portionPercent,
          dmsSyncStatus: a.dmsSyncStatus,
          dmsSyncDate: a.dmsSyncDate
        }));
      } else {
        this.rows = [{ employeeNo: '', portionPercent: 100 }];
      }
    } finally {
      this.spinner.hide();
    }
  }

  get portionTotal(): number {
    return this.rows.reduce((sum, r) => sum + (Number(r.portionPercent) || 0), 0);
  }

  get isPortionValid(): boolean {
    return this.portionTotal === 100;
  }

  get hasZeroPortionRow(): boolean {
    return this.rows.some(r => Number(r.portionPercent) <= 0);
  }

  get hasDuplicate(): boolean {
    const codes = this.rows.map(r => r.employeeNo).filter(e => e);
    return new Set(codes).size !== codes.length;
  }

  isRowDuplicate(index: number): boolean {
    const employeeNo = this.rows[index].employeeNo;
    if (!employeeNo) return false;
    return this.rows.some((r, i) => i !== index && r.employeeNo === employeeNo);
  }

  get canConfirm(): boolean {
    return this.rows.length > 0
      && !this.hasDuplicate
      && this.rows.every(r => !!r.employeeNo);
  }

  get maxResources(): number {
    if (this.costType === 'Personnel') return 5;
    if (this.costType === 'Material' || this.costType === 'ExtService') return 1;
    return 0;
  }

  get canAddRow(): boolean {
    return this.rows.length < this.maxResources;
  }

  get hasUnsyncedRows(): boolean {
    if (this.rows.some(r => !(r.id > 0))) return false;
    return this.rows.some(r => r.dmsSyncStatus === 0 || r.dmsSyncStatus === 2);
  }

  get primaryMechanic(): string {
    if (!this.rows.length) return '';
    const top = [...this.rows].sort((a, b) => b.portionPercent - a.portionPercent)[0];
    const resource = this.availableResources.find(r => r.resourceNo === top.employeeNo);
    return resource ? resource.resourceName : top.employeeNo;
  }

  getMechanicName(employeeNo: string): string {
    const r = this.availableResources.find(x => x.resourceNo === employeeNo);
    return r ? r.resourceName : '';
  }

  onPortionChange(changedIndex: number): void {
    const val = Number(this.rows[changedIndex].portionPercent) || 0;
    this.rows[changedIndex].portionPercent = Math.min(100, Math.max(0, val));
  }

  addRow(): void {
    if (!this.canAddRow) {
      const key = this.costType === 'Personnel' ? 'lblRuleMaxFiveResources' : 'lblRuleMaxOneResource';
      this.toastr.warning(this.translate.instant(key));
      return;
    }
    this.rows = [...this.rows, { employeeNo: '', portionPercent: 0 }];
  }

  removeRow(index: number): void {
    if (index === 0 && this.rows.length > 1) {
      this.toastr.warning(this.translate.instant('lblPrimaryResourceCannotBeRemoved'));
      return;
    }
    this.rows.splice(index, 1);
    this.redistributeEqually();
  }

  private redistributeEqually(): void {
    if (this.rows.length === 0) return;
    const perRow = Math.floor(100 / this.rows.length);
    let distributed = 0;
    this.rows.forEach((row, i) => {
      if (i === this.rows.length - 1) {
        row.portionPercent = 100 - distributed;
      } else {
        row.portionPercent = perRow;
        distributed += perRow;
      }
    });
  }

  async confirm(): Promise<void> {
    if (!this.canConfirm || this.isSaving) return;
    if (!this.isPortionValid) {
      this.toastr.warning(this.translate.instant('lblTotalPortionMustBe100'));
      return;
    }
    if (this.hasZeroPortionRow) {
      this.toastr.warning(this.translate.instant('lblPortionMustBeGreaterThanZero'));
      return;
    }
    this.isSaving = true;
    this.spinner.show();
    try {
      const request: MechanicLineAssignmentSaveRequest = {
        ifsWorkOrderNumber: this.ifsWorkOrderNumber,
        appointmentId: this.appointmentId,
        appointmentJobId: this.appointmentJobId,
        appointmentJobDetailId: this.appointmentJobDetailId,
        isManual: true,
        mechanics: this.rows.map(r => ({
          dmsLineNo: this.dmsLineNo,
          employeeNo: r.employeeNo,
          portionPercent: Number(r.portionPercent),
          isManual: true
        }))
      };
      const response = await this.dataService.saveMechanicLineAssignment(request);
      if (response && response.responseCode === 200) {
        this.toastr.success(this.translate.instant('lblMechanicAssignmentSavedSuccess'));
        // Reload from server so rows carry the DMS sync status written during save
        try {
          const refreshed = await this.dataService.getMechanicLineAssignmentByDetail(
            this.appointmentId, this.appointmentJobDetailId);
          const synced: MechanicLineAssignmentItem[] = refreshed?.responseObject || [];
          this.rows = this.rows.map(r => {
            const updated = synced.find(s => s.employeeNo === r.employeeNo);
            return updated ? { ...r, id: updated.id, dmsSyncStatus: updated.dmsSyncStatus, dmsSyncDate: updated.dmsSyncDate } : r;
          });
        } catch { /* non-fatal — close with current rows if reload fails */ }
        this.activeModal.close(this.rows);
      } else {
        this.toastr.error(response?.responseMessage || this.translate.instant('lblSaveError'));
      }
    } catch {
      this.toastr.error(this.translate.instant('lblSaveError'));
    } finally {
      this.isSaving = false;
      this.spinner.hide();
    }
  }

  async syncAll(): Promise<void> {
    if (this.isSaving) return;
    if (!this.isPortionValid) {
      this.toastr.warning(this.translate.instant('lblTotalPortionMustBe100'));
      return;
    }
    if (this.hasZeroPortionRow) {
      this.toastr.warning(this.translate.instant('lblPortionMustBeGreaterThanZero'));
      return;
    }
    this.isSaving = true;
    this.spinner.show();
    try {
      const response = await this.dataService.syncMechanicLineAssignment(this.appointmentId, this.appointmentJobDetailId);
      if (response && response.responseCode === 200) {
        this.toastr.success(this.translate.instant('lblMechanicAssignmentSavedSuccess'));
        // Refresh sync status on rows from the response
        const synced: MechanicLineAssignmentItem[] = response.responseObject || [];
        this.rows = this.rows.map(r => {
          const updated = synced.find(s => s.employeeNo === r.employeeNo);
          return updated ? { ...r, id: updated.id, dmsSyncStatus: updated.dmsSyncStatus, dmsSyncDate: updated.dmsSyncDate } : r;
        });
      } else {
        this.toastr.error(response?.responseMessage || this.translate.instant('lblSaveError'));
      }
    } catch {
      this.toastr.error(this.translate.instant('lblSaveError'));
    } finally {
      this.isSaving = false;
      this.spinner.hide();
    }
  }

  async deleteAll(): Promise<void> {
    if (this.isSaving) return;

    const confirmRef = this.modalService.open(NgbdModalContent);
    confirmRef.componentInstance.modelHeader = this.translate.instant('lblConfirmation');
    confirmRef.componentInstance.modelBody = this.translate.instant('lblConfirmDeleteResourceAssignment');

    let confirmed = false;
    try { await confirmRef.result; } catch (reason) { confirmed = reason === true; }
    if (!confirmed) return;

    this.isSaving = true;
    this.spinner.show();
    try {
      const request: MechanicLineAssignmentSaveRequest = {
        ifsWorkOrderNumber: this.ifsWorkOrderNumber,
        appointmentId: this.appointmentId,
        appointmentJobId: this.appointmentJobId,
        appointmentJobDetailId: this.appointmentJobDetailId,
        isManual: true,
        isDeleted: true,
        mechanics: []
      };
      const response = await this.dataService.saveMechanicLineAssignment(request);
      if (response && response.responseCode === 200) {
        this.toastr.success(this.translate.instant('lblMechanicAssignmentSavedSuccess'));
        this.activeModal.close([]);
      } else {
        this.toastr.error(response?.responseMessage || this.translate.instant('lblSaveError'));
      }
    } catch {
      this.toastr.error(this.translate.instant('lblSaveError'));
    } finally {
      this.isSaving = false;
      this.spinner.hide();
    }
  }

  dismiss(): void {
    this.activeModal.dismiss();
  }

  trackByIndex(index: number): number {
    return index;
  }
}
